import { verifyPublicAssets } from '@maipo/format';
import { walkLayers, type EditorDocument, type TextLayer } from '../core/model';
import { throwIfAborted } from '../codecs/types';
import { documentPublicResources } from './adapter';
import { resourceValidationIds } from './resource-validation-ids';
import { validateNativePortableText } from './native-text-contract';
import type { PortableTextLayoutInput } from '../vector/portable-text-layout';
import type { PortableTextPaint } from '../vector/portable-text-paint';

interface ResourceBinding { font: string; asset: string; data: unknown }
interface OwnedPaint { paint: PortableTextPaint; refs: number; bytes: number }
interface Entry { layerId: string; documentId: string; text: string; snapshot: string; settings: string; resources: ResourceBinding[]; owned: OwnedPaint }
const prepared = new WeakMap<EditorDocument, Map<string, Entry>>();
const generations = new WeakMap<EditorDocument, number>();
function own(paint: PortableTextPaint): OwnedPaint { return { paint, refs: 1, bytes: paint.retainedBytes() }; }
function drop(owned: OwnedPaint): void { if (--owned.refs === 0) owned.paint.dispose(); }
/** Opaque verified state. The lease and an active document each own a reference. */
export interface NativeTextLease { readonly state: Entry; readonly bytes: number; readonly paint: PortableTextPaint; release(): void }
function lease(entry: Entry): NativeTextLease {
  entry.owned.refs++;
  let released = false;
  return { state: entry, bytes: entry.owned.bytes + entry.snapshot.length * 2,
    get paint() { if (released) throw new Error('Portable text lease was released.'); return entry.owned.paint; },
    release() { if (!released) { released = true; drop(entry.owned); } } };
}
export function retainNativeText(document: EditorDocument, layer: TextLayer): NativeTextLease {
  nativeDocumentTextPaint(document, layer);
  return lease(prepared.get(document)!.get(layer.id)!);
}
/** Validate the proposed authored layer before switching the active paint. */
export function activateNativeText(document: EditorDocument, layer: TextLayer, state: NativeTextLease['state']): void {
  if (state.owned.refs <= 0 || state.documentId !== document.id || state.layerId !== layer.id
    || state.text !== layer.text.value || state.snapshot !== layer.text.portable?.snapshot
    || state.settings !== settings(layer) || !sameResources(state.resources, resources(document, layer))) throw new Error('Portable text state does not match its target.');
  let entries = prepared.get(document);
  if (!entries) { entries = new Map(); prepared.set(document, entries); }
  const previous = entries.get(layer.id);
  if (previous === state) return;
  state.owned.refs++; entries.set(layer.id, state);
  if (previous) drop(previous.owned);
  generations.set(document, (generations.get(document) ?? 0) + 1);
}
/** A copied map prevents later single-layer activations from mutating history. */
export function retainNativeDocumentTextState(document: EditorDocument): { state: ReadonlyMap<string, Entry>; bytes: number; release(): void } {
  const state = new Map(prepared.get(document));
  const leases = [...state.values()].map(lease);
  let released = false;
  return { state, bytes: leases.reduce((bytes, item) => bytes + item.bytes, 0),
    release() { if (!released) { released = true; leases.forEach(item => item.release()); } } };
}
/** Switch an entire document projection, retaining inactive states for undo. */
export function activateNativeDocumentTextState(document: EditorDocument, state: ReadonlyMap<string, Entry>): void {
  for (const entry of state.values()) if (entry.owned.refs <= 0 || entry.documentId !== document.id) throw new Error('Portable text state belongs to another document or was released.');
  for (const layer of walkLayers(document.root)) {
    if (layer.kind !== 'text' || layer.text.schemaVersion !== 2) continue;
    const entry = state.get(layer.id);
    if (!entry || entry.layerId !== layer.id || entry.text !== layer.text.value || entry.snapshot !== layer.text.portable?.snapshot
      || entry.settings !== settings(layer) || !sameResources(entry.resources, resources(document, layer))) throw new Error('Portable text state does not match the document.');
  }
  const next = new Map(state);
  for (const entry of next.values()) entry.owned.refs++;
  releaseNativeDocumentText(document); prepared.set(document, next);
}
/** Candidate documents borrow only independently owned, verified existing entries. */
export function inheritNativeDocumentText(source: EditorDocument, candidate: EditorDocument): void {
  const next = new Map(prepared.get(source));
  for (const entry of next.values()) {
    if (entry.documentId !== candidate.id) throw new Error('Portable text inheritance requires the same document identity.');
  }
  for (const entry of next.values()) entry.owned.refs++;
  releaseNativeDocumentText(candidate); prepared.set(candidate, next);
}
/** Prepare a detached authored edit; neither the live layer nor document is changed. */
export async function prepareNativeTextEdit(document: EditorDocument, layer: TextLayer, signal?: AbortSignal): Promise<{ text: TextLayer['text']; lease: NativeTextLease }> {
  throwIfAborted(signal);
  const capturedLayer = structuredClone(layer);
  const captured = { ...document, extensions: structuredClone(document.extensions), root: { ...document.root, children: [capturedLayer] } };
  const { preparePortableTextPaint } = await import('../vector/portable-text-paint');
  const result = await preparePortableTextPaint(await nativePortableTextInput(captured, capturedLayer, signal), signal ? { signal } : {});
  capturedLayer.text.portable!.snapshot = result.snapshot;
  const entry: Entry = { layerId: capturedLayer.id, documentId: captured.id, text: capturedLayer.text.value,
    snapshot: result.snapshot, settings: settings(capturedLayer), resources: resources(captured, capturedLayer), owned: own(result.paint) };
  const retained = lease(entry); drop(entry.owned);
  return { text: capturedLayer.text, lease: retained };
}
function settings(layer: TextLayer): string {
  const portable = { ...layer.text.portable, snapshot: undefined };
  return JSON.stringify({ portable, width: layer.text.box.width, autoHeight: layer.text.box.autoHeight,
    family: layer.text.style.family, weight: layer.text.style.weight, size: layer.text.style.size,
    tracking: layer.text.style.tracking, align: layer.text.style.align, lineHeight: layer.text.style.lineHeight });
}
function resources(document: EditorDocument, layer: TextLayer): ResourceBinding[] {
  const metadata = document.extensions['maipo:public-document'] as { fonts?: Array<{ id: string; source?: { asset?: string } }>; assets?: Array<{ id: string; data?: unknown }> } | undefined;
  if (!Array.isArray(metadata?.fonts) || !Array.isArray(metadata.assets)) throw new Error('Portable text resources are missing.');
  return layer.text.portable!.fonts.map(ref => {
    const font = metadata.fonts!.find(font => font.id === ref);
    const asset = metadata.assets!.find(asset => asset.id === font?.source?.asset);
    return { font: JSON.stringify(font), asset: JSON.stringify({ ...asset, data: undefined }), data: asset?.data };
  });
}
function sameResources(a: ResourceBinding[], b: ResourceBinding[]): boolean {
  return a.length === b.length && a.every((item, index) => item.font === b[index]!.font && item.asset === b[index]!.asset && item.data === b[index]!.data);
}
async function identity(value: string, prefix: string): Promise<string> {
  // TextEncoder replaces lone surrogates with U+FFFD. Reject that ambiguity
  // rather than binding distinct native identities to the same snapshot ID.
  for (let index = 0; index < value.length; index++) {
    const unit = value.charCodeAt(index);
    if (unit >= 0xd800 && unit <= 0xdbff) {
      const low = value.charCodeAt(++index);
      if (!(low >= 0xdc00 && low <= 0xdfff)) throw new Error('Portable text identities must contain valid Unicode.');
    } else if (unit >= 0xdc00 && unit <= 0xdfff) throw new Error('Portable text identities must contain valid Unicode.');
  }
  const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return prefix + [...new Uint8Array(bytes)].map(byte => byte.toString(16).padStart(2, '0')).join('');
}
export async function nativePortableTextInput(document: EditorDocument, layer: TextLayer, signal?: AbortSignal): Promise<PortableTextLayoutInput> {
  validateNativePortableText(layer.text);
  const portable = layer.text.portable;
  if (!portable) throw new Error('Text does not have portable settings.');
  const retained = documentPublicResources(document);
  const id = resourceValidationIds(retained.fonts, retained.assets);
  const errors = (await verifyPublicAssets({ format: 'maipo-scene', version: '0.1', documentId: id(), revision: 0, name: 'Portable text resources',
    canvas: { width: 1, height: 1, colorSpace: 'srgb', background: { type: 'transparent' } }, ...retained, children: [] })).filter(item => item.severity === 'error');
  if (errors.length) throw new Error('Portable text resources fail asset verification.');
  throwIfAborted(signal);
  const fonts = portable.fonts.map((ref, index) => {
    const font = retained.fonts.find(font => font.id === ref);
    if (!font || font.source.kind !== 'embedded' || font.style !== 'normal') throw new Error('Portable text requires exact embedded normal-style fonts.');
    if (index === 0 && (font.family !== layer.text.style.family || font.weight !== layer.text.style.weight)) throw new Error('Portable text primary font differs from its authored style.');
    const assetId = font.source.asset;
    const asset = retained.assets.find(asset => asset.id === assetId);
    if (!asset || !asset.mediaType.startsWith('font/')) throw new Error('Portable text font asset is missing.');
    const decoded = atob(asset.data);
    return { fontRef: ref, fontBytes: Uint8Array.from(decoded, character => character.charCodeAt(0)) };
  });
  return { documentId: await identity(document.id, 'native.d.'), nodeId: await identity(layer.id, 'native.n.'), revision: portable.revision,
    text: layer.text.value, fonts, fontSize: layer.text.style.size, width: layer.text.box.width, topLeft: { x: 0, y: 0 },
    metricsPolicy: portable.metricsPolicy, lineGap: portable.lineGap, overflow: portable.overflow, emptyLineFontIndex: 0,
    baseDirection: portable.baseDirection, language: portable.language, scriptLanguages: structuredClone(portable.scriptLanguages) };
}
export function nativeDocumentTextGeneration(document: EditorDocument): number { return generations.get(document) ?? 0; }
export function releaseNativeDocumentText(document: EditorDocument): void {
  generations.set(document, (generations.get(document) ?? 0) + 1);
  for (const entry of prepared.get(document)?.values() ?? []) drop(entry.owned);
  prepared.delete(document);
}
/** Only publish a complete verified candidate; preserve prior paint on failure. */
export async function prepareNativeDocumentText(document: EditorDocument, signal?: AbortSignal): Promise<void> {
  throwIfAborted(signal);
  const layers = walkLayers(document.root).filter((layer): layer is TextLayer => layer.kind === 'text' && layer.text.schemaVersion === 2);
  if (!layers.length) { releaseNativeDocumentText(document); return; }
  const generation = generations.get(document) ?? 0;
  const docId = document.id;
  for (const layer of layers) validateNativePortableText(layer.text);
  const captured = layers.map(layer => ({ layer, id: layer.id, value: layer.text.value, snapshot: layer.text.portable?.snapshot ?? '', settings: settings(layer), resources: resources(document, layer) }));
  const next = new Map<string, Entry>();
  try {
    const { preparePortableTextPaint } = await import('../vector/portable-text-paint');
    for (const item of captured) {
      // Reuse only states whose full binding still matches. Fresh opens have no
      // cache; staging projections can share already admitted destination text.
      let existing: Entry | undefined;
      try { nativeDocumentTextPaint(document, item.layer); existing = prepared.get(document)?.get(item.id); } catch { /* Recompute below. */ }
      if (existing) { existing.owned.refs++; next.set(item.id, existing); continue; }
      const input = await nativePortableTextInput(document, item.layer, signal);
      const result = await preparePortableTextPaint(input, { snapshot: item.snapshot, ...(signal ? { signal } : {}) });
      next.set(item.id, { layerId: item.id, documentId: docId, text: item.value, snapshot: item.snapshot, settings: item.settings, resources: item.resources, owned: own(result.paint) });
    }
    throwIfAborted(signal);
    const current = walkLayers(document.root).filter(layer => layer.kind === 'text' && layer.text.schemaVersion === 2);
    if (current.length !== layers.length || current.some((layer, index) => layer !== layers[index])
      || (generations.get(document) ?? 0) !== generation || document.id !== docId
      || captured.some(item => item.layer.id !== item.id || item.layer.text.value !== item.value || item.layer.text.portable?.snapshot !== item.snapshot || settings(item.layer) !== item.settings || !sameResources(resources(document, item.layer), item.resources))) throw new Error('Portable text changed while paint resources were prepared.');
    releaseNativeDocumentText(document); prepared.set(document, next);
  } catch (error) { for (const entry of next.values()) drop(entry.owned); throw error; }
}
export function nativeDocumentTextPaint(document: EditorDocument | undefined, layer: TextLayer): PortableTextPaint {
  const entry = document && prepared.get(document)?.get(layer.id);
  if (!entry || entry.layerId !== layer.id || document?.id !== entry.documentId || layer.text.value !== entry.text || layer.text.portable?.snapshot !== entry.snapshot || settings(layer) !== entry.settings || !sameResources(resources(document, layer), entry.resources)) throw new Error('Portable text paint is missing or stale; reopen or recompute the layout.');
  return entry.owned.paint;
}

/** Saving verifies current bindings without replacing resources retained for undo. */
export async function verifyNativeDocumentText(document: EditorDocument, signal?: AbortSignal): Promise<void> {
  throwIfAborted(signal);
  // Capture every unprepared layer before yielding, just like the archive writer.
  // The save belongs to the clicked revision even if editing continues meanwhile.
  const pending: TextLayer[] = [];
  for (const layer of walkLayers(document.root)) {
    if (layer.kind !== 'text' || layer.text.schemaVersion !== 2) continue;
    validateNativePortableText(layer.text);
    try { nativeDocumentTextPaint(document, layer); } catch { pending.push(structuredClone(layer)); }
  }
  if (!pending.length) return;
  const captured: EditorDocument = { ...document, extensions: structuredClone(document.extensions), root: { ...document.root, children: pending } };
  const { admitPortableTextSnapshot } = await import('../vector/portable-text-snapshot');
  for (const layer of pending) {
    await admitPortableTextSnapshot(layer.text.portable!.snapshot, await nativePortableTextInput(captured, layer, signal), signal);
    throwIfAborted(signal);
  }
}
